import React from 'react';
import Markdown from 'react-markdown';
import { Box, Typography } from '@mui/material';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

interface DocumentMarkdownRendererProps {
  markdown: string;
  sx?: object;
}

/**
 * Document-level markdown renderer for clinic documents.
 * Uses react-markdown with remark-gfm (tables) and rehype-raw (HTML like <br /> in cells).
 * Renders faithfully without transforming list numbering or indentation.
 */
export const DocumentMarkdownRenderer: React.FC<DocumentMarkdownRendererProps> = ({
  markdown,
  sx,
}) => {
  return (
    <Box
      sx={{
        color: 'text.primary',
        lineHeight: 1.7,
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        ...sx,
      }}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          p: ({ children }) => (
            <Typography
              component="p"
              variant="body2"
              sx={{ fontSize: '0.875rem', lineHeight: 1.7, my: 1 }}
            >
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}
            >
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography
              component="h2"
              variant="h6"
              sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{ fontWeight: 700, mt: 2, mb: 0.75 }}
            >
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography
              component="h4"
              variant="subtitle2"
              sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}
            >
              {children}
            </Typography>
          ),
          h5: ({ children }) => (
            <Typography
              component="h5"
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 1.5, mb: 0.5, fontSize: '0.8125rem' }}
            >
              {children}
            </Typography>
          ),
          h6: ({ children }) => (
            <Typography
              component="h6"
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 1, mb: 0.5, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              {children}
            </Typography>
          ),
          strong: ({ children }) => (
            <strong style={{ fontWeight: 600 }}>{children}</strong>
          ),
          em: ({ children }) => (
            <em style={{ fontStyle: 'italic' }}>{children}</em>
          ),
          ul: ({ children }) => (
            <Box
              component="ul"
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.7,
                my: 1,
                pl: 3,
                '& ul, & ol': { my: 0.25 },
              }}
            >
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box
              component="ol"
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.7,
                my: 1,
                pl: 3,
                '& ul, & ol': { my: 0.25 },
              }}
            >
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Box
              component="li"
              sx={{ fontSize: '0.875rem', lineHeight: 1.7, my: 0.25, '& > p': { my: 0 } }}
            >
              {children}
            </Box>
          ),
          blockquote: ({ children }) => (
            <Box
              component="blockquote"
              sx={{
                borderLeft: 3,
                borderColor: 'primary.light',
                pl: 2,
                my: 1.5,
                color: 'text.secondary',
                fontStyle: 'italic',
                '& p': { my: 0.5 },
              }}
            >
              {children}
            </Box>
          ),
          code: ({ children, className }) => {
            const isBlock = className?.startsWith('language-');
            if (isBlock) {
              return (
                <Box
                  component="code"
                  sx={{
                    display: 'block',
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {children}
                </Box>
              );
            }
            return (
              <Box
                component="code"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                  bgcolor: 'grey.100',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                }}
              >
                {children}
              </Box>
            );
          },
          pre: ({ children }) => (
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.50',
                border: 1,
                borderColor: 'grey.200',
                borderRadius: 1.5,
                p: 2,
                my: 1.5,
                overflow: 'auto',
                fontSize: '0.8125rem',
              }}
            >
              {children}
            </Box>
          ),
          hr: () => (
            <Box
              component="hr"
              sx={{
                border: 'none',
                borderTop: 1,
                borderColor: 'divider',
                my: 2,
              }}
            />
          ),
          table: ({ children }) => (
            <Box sx={{ overflowX: 'auto', my: 2 }}>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.8125rem',
                  lineHeight: 1.5,
                  '& th, & td': {
                    border: 1,
                    borderColor: 'grey.300',
                    px: 1.5,
                    py: 1,
                    textAlign: 'left',
                    verticalAlign: 'top',
                  },
                  '& th': {
                    bgcolor: 'grey.100',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  },
                  '& tr:nth-of-type(even)': {
                    bgcolor: 'grey.50',
                  },
                  '& tr:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {children}
              </Box>
            </Box>
          ),
          thead: ({ children }) => <thead>{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr>{children}</tr>,
          th: ({ children }) => <th>{children}</th>,
          td: ({ children }) => <td>{children}</td>,
          a: ({ children, href }) => (
            <Box
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                '&:hover': { color: 'primary.dark' },
              }}
            >
              {children}
            </Box>
          ),
        }}
      >
        {markdown}
      </Markdown>
    </Box>
  );
};
